/*
 * The preview's invoices, payments, quotes and leads (draft 3), with the same shapes and rules as the real server: visits
 * marked done land on an invoice in To check, Confirm and send writes the exact text to the outbox, the pay page takes a
 * tip and pays once, edit after paid asks Credit or Don't credit, and only the owner charges a saved card. Stripe is
 * pretend here; nothing is charged or sent. A reload starts over.
 */
import {
  addDays,
  AGENCY_STAGES,
  proposalCalc,
  proposalJobFrequency,
  proposalLines,
  proposalPicked,
  proposalPublic,
  proposalSaveBody,
  proposalTerms,
  proposalTitle,
  proposalWalk,
  proposalWordsOf,
  type ProposalDetail,
  type ProposalRow,
  type ProposalSettings,
  type ProposalWalk,
  type ProposalWords,
  agreementSplit,
  agreementWords,
  autoPayWords,
  editPaid,
  INVOICE_STATES,
  invoiceEditPaidBody,
  invoiceMessage,
  invoiceNewBody,
  invoicePatch,
  KLARNA_MIN_CENTS,
  mergeCheck,
  mergeInvoicesBody,
  takeOffCheck,
  takeVisitOffBody,
  invoiceState,
  invoiceTotals,
  isBusiness,
  leadInput,
  leadPatch,
  dueToMove,
  hiddenByRules,
  NO_STAGE_RULES,
  quoteStats,
  stageRulesOf,
  stageRulesShape,
  type StageRules,
  maxTip,
  pastDueAt,
  payDoneBody,
  payStartBody,
  quoteApproveBody,
  quoteConvertBody,
  quoteInput,
  quoteMessage,
  quotePatch,
  quoteSendBody,
  quoteTotals,
  recordPaymentBody,
  saveCardSentence,
  sendBody,
  shortName,
  tipChoices,
  tipsOn,
  chargeCardBody,
  type Action,
  type CardOnFile,
  type Deposit,
  type InvoiceDetail,
  type InvoiceRow,
  type InvoiceSummary,
  type InvoiceState,
  type LeadRow,
  type MoneyToDo,
  type PaidWork,
  type PaymentDetail,
  type PaymentList,
  type PaymentListRow,
  paidThroughWords,
  type PaymentMethod,
  type PaymentRow,
  type PublicInvoice,
  type PublicQuote,
  type QuoteDetail,
  type QuoteRow,
  type QuoteStatus,
  type RequestDetail,
  type RequestRow,
  type NoteRow,
  requestArchived,
  requestState,
  type SendPreview,
  type WaitingWork,
  FEED_ACCESS,
  FEED_KINDS,
  formatMoney,
  type Cents,
  type FeedItem,
  type FeedKind,
  lookFor,
  invoiceRank,
  filledSections,
  isSectionFile,
  quoteHeaderBody,
  quoteSectionsBody,
  sectionFileIds,
  type QuoteSection,
  type SectionFile,
  policiesToSign,
  SETTINGS_DEFAULTS,
  splitAddress,
} from "@steward/shared";
import type { z } from "zod";
import { clientMoneyHook, visitHooks, workLookup, type DoneVisit, type WorkClient } from "./work";

type Answer = Promise<{ status: number; body: unknown }>;
const ok = (body: unknown, status = 200): Answer => Promise.resolve({ status, body });
const fail = (status: number, code: string, message: string, extra: Record<string, unknown> = {}): Answer =>
  Promise.resolve({ status, body: { code, message, ...extra } });
const NOT_ALLOWED = () => fail(403, "not_allowed", "Your role can't do that. Ask the owner if you need it.");
const NOT_FOUND = (what: string) => fail(404, "not_found", `That ${what} was not found.`);
const bad = (e: z.ZodError) => fail(400, "bad_request", e.issues[0]?.message ?? "Something in the request is not right.");

/** What money needs to know about a company: its brand words and settings. */
export interface MoneyBrand {
  name: string;
  color: string | null;
  /** The brand kit (Settings, Brand). */
  ctaColor: string | null;
  secondaryColor: string | null;
  accentColor: string | null;
  logoUrl: string | null;
  logoWhiteUrl: string | null;
  phone: string | null;
  email: string | null;
  trade: string;
  timezone: string;
  tips: boolean;
  invoiceText: string;
  invoiceSubject: string;
  pastDueHoursHome: number;
  pastDueDaysBusiness: number;
  stripe: boolean;
}
export interface MoneyCtx {
  companyId: string;
  me: string;
  isOwner: boolean;
  gate: (a: Action) => boolean;
  brand: MoneyBrand;
  /** The company's team, for who worked an invoice typed by hand. */
  people: readonly { id: string; firstName: string; lastName: string }[];
  queue: (m: { channel: "sms" | "email"; to: string; subject: string | null; body: string; template: string }) => void;
  log: (action: string) => void;
}

interface ILine {
  id: string;
  visitId: string | null;
  description: string;
  note: string | null;
  quantity: number;
  unitPriceCents: number;
  amountCents: number;
  serviceDate: string | null;
  propertyLabel: string | null;
}
interface Inv {
  companyId: string;
  id: string;
  number: number | null;
  clientId: string;
  status: "to_check" | "to_collect" | "sent" | "paid" | "no_charge" | "void";
  token: string;
  lines: ILine[];
  visits: { ref: string; date: string; jobName: string }[];
  jobName: string | null;
  tipCents: number;
  poNumber: string | null;
  noteToClient: string | null;
  payNote: string | null;
  debt: boolean;
  remindersOff: boolean;
  checkMailedAt?: string | null;
  firstSentAt: string | null;
  sentAt: string | null;
  sentVia: string | null;
  sentTo: string | null;
  issuedOn: string | null;
  dueAt: string | null;
  crewCollected: string | null;
  /** Who worked an invoice typed by hand (no visits), so payroll pays them. */
  crew: { id: string; name: string }[];
  edits: { kind: string; fromTotalCents: number; toTotalCents: number; what: string | null; by: string | null; at: string }[];
  createdAt: string;
  /** This invoice's own ways to pay; unset uses the preview's settings (card only). */
  payWays?: { card: boolean; bank: boolean; klarna: boolean } | null;
}
interface Pay {
  companyId: string;
  id: string;
  invoiceId: string | null;
  clientId: string;
  amountCents: number;
  tipCents: number;
  method: PaymentMethod;
  checkNumber: string | null;
  receivedAt: string;
  recordedBy: string | null;
  note: string | null;
}
interface QLine {
  id: string;
  description: string;
  note: string | null;
  quantity: number;
  unitPriceCents: number;
  amountCents: number;
  optional: boolean;
  picked: boolean;
  recurring: boolean;
  bill: "once" | "monthly";
}
interface Quote {
  companyId: string;
  id: string;
  number: number;
  clientId: string | null;
  leadId: string | null;
  contactName: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  address: string | null;
  title: string | null;
  message: string | null;
  status: QuoteStatus;
  expiresOn: string | null;
  deposit: Deposit | null;
  frequency: string;
  lines: QLine[];
  token: string;
  sentAt: string | null;
  approvedAt: string | null;
  signature: { name: string; at: string; drawing: string | null } | null;
  jobId: string | null;
  depositInvoiceId: string | null;
  createdAt: string;
  /** Proposals carry their terms. */
  terms?: string | null;
  archived?: boolean;
  sections?: QuoteSection[];
}

/** Pictures and files added to quote sections in the preview, with the bytes as a data link. */
const quoteFiles = new Map<string, SectionFile & { quoteId: string; data: string }>();
const filesOfSections = (sections: readonly QuoteSection[]): SectionFile[] =>
  sectionFileIds(sections).flatMap((fid) => {
    const f = quoteFiles.get(fid);
    return f ? [{ id: f.id, name: f.name, mime: f.mime, size: f.size }] : [];
  });
/** A quote section picture for an <img> in the preview (office files/<id>, or the client page's public link). */
export const quoteFilePicture = (path: string): string | null => quoteFiles.get(path.split("/").pop() ?? "")?.data ?? null;
interface Stage {
  companyId: string;
  id: string;
  name: string;
  position: number;
  fixed: "won" | "lost" | null;
  /** The column's own settings (Quotes, column settings). */
  rules?: StageRules;
}
interface Lead {
  companyId: string;
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  address: string | null;
  service: string | null;
  valueCents: number | null;
  source: string | null;
  stageId: string;
  clientId: string | null;
  quoteId: string | null;
  note: string | null;
  businessName?: string | null;
  website?: string | null;
  assignedTo?: string | null;
  /** Archived from Requests: off the lists and the pipeline. */
  archived?: boolean;
  stageChangedAt: string;
  createdAt: string;
}

const invoices: Inv[] = [];
/** Office notes on each request, by lead id. */
const leadNotes = new Map<string, NoteRow[]>();
const payments: Pay[] = [];
const quotes: Quote[] = [];
const stages: Stage[] = [];
const leads: Lead[] = [];
const credits = new Map<string, number>();
const cards = new Map<string, CardOnFile>();
const autoCharge = new Map<string, boolean>();
const numbers = new Map<string, number>();
/** Pay page sessions waiting to come back from pretend Stripe: session id to the tip and save-card choice. */
const sessions = new Map<string, { invoiceId: string; tipCents: number; saveCard: boolean }>();

let newId: () => string = () => String(Math.random());
let TODAY = "";
const token = () => Array.from({ length: 64 }, () => "0123456789abcdef"[Math.floor(Math.random() * 16)] ?? "0").join("");
const nextNumber = (companyId: string, kind: "invoice" | "quote") => {
  const k = `${companyId}:${kind}`;
  const n = numbers.get(k) ?? 1001;
  numbers.set(k, n + 1);
  return n;
};
const iso = (daysAgo: number, hour = 10) => {
  const d = new Date(`${addDays(TODAY, -daysAgo)}T12:00:00Z`);
  d.setUTCHours(hour + 5);
  return d.toISOString();
};
const appLink = (kind: "pay" | "quote", t: string) => `https://app.thetrades.example/${kind}/${t}`;

// Invoice math -------------------------------------------------------------------------------------------------------

const paidOf = (inv: Inv) => payments.filter((p) => p.invoiceId === inv.id).reduce((s, p) => s + p.amountCents, 0);
const totalsOf = (inv: Inv) => {
  const paid = paidOf(inv);
  const t = invoiceTotals(inv.lines, inv.tipCents, paid);
  return { ...t, paidCents: paid };
};
function settle(inv: Inv): void {
  const t = totalsOf(inv);
  if (inv.status === "void" || inv.status === "no_charge" || inv.status === "to_check") return;
  if (t.totalCents > 0 && t.balanceCents <= 0) inv.status = "paid";
  else if (inv.status === "paid" && t.balanceCents > 0) inv.status = "sent";
}
const clientOf = (inv: Inv): WorkClient =>
  workLookup.client(inv.clientId) ?? {
    id: inv.clientId,
    companyId: inv.companyId,
    name: "Client",
    firstName: "there",
    phone: null,
    email: null,
    companyName: null,
    address: null,
  };
const stateOf = (inv: Inv): InvoiceState => {
  const t = totalsOf(inv);
  return invoiceState(
    { status: inv.status, balanceCents: t.balanceCents, dueAt: inv.dueAt ? new Date(inv.dueAt) : null, debt: inv.debt },
    new Date(),
  );
};
function invoiceRow(inv: Inv): InvoiceRow {
  const t = totalsOf(inv);
  return {
    id: inv.id,
    number: inv.number,
    clientId: inv.clientId,
    clientName: clientOf(inv).name,
    status: inv.status,
    state: stateOf(inv),
    issuedOn: inv.issuedOn,
    dueAt: inv.dueAt,
    sentAt: inv.sentAt,
    subtotalCents: t.subtotalCents,
    tipCents: inv.tipCents,
    totalCents: t.totalCents,
    paidCents: t.paidCents,
    balanceCents: t.balanceCents,
    visitCount: inv.visits.length,
    firstServiceDate:
      inv.lines
        .map((l) => l.serviceDate)
        .filter((d): d is string => Boolean(d))
        .sort()[0] ?? null,
    jobName: inv.jobName,
    subject: inv.jobName ?? inv.lines.map((l) => l.description).join(", "),
    onTheWay: Boolean(inv.checkMailedAt),
    // Made up for the preview: every other sent invoice was opened.
    viewed: inv.sentAt !== null && inv.status === "sent" && (inv.number ?? 0) % 2 === 1,
    notSent: inv.firstSentAt === null && (inv.status === "paid" || inv.status === "no_charge" || inv.status === "void"),
  };
}
const listRow = (p: Pay): PaymentListRow => ({
  ...paymentRow(p),
  refund: p.amountCents < 0,
  online: p.method === "card" || p.method === "bank" || p.method === "klarna",
  paidThrough: paidThroughWords({
    method: p.method,
    online: p.method === "card" || p.method === "bank" || p.method === "klarna",
    crewCollected: false,
    refund: p.amountCents < 0,
    recordedBy: p.recordedBy,
  }),
});
const paymentRow = (p: Pay): PaymentRow => {
  const inv = invoices.find((i) => i.id === p.invoiceId);
  return {
    id: p.id,
    invoiceId: p.invoiceId,
    invoiceNumber: inv?.number ?? null,
    clientId: p.clientId,
    clientName: workLookup.client(p.clientId)?.name ?? "Client",
    amountCents: p.amountCents,
    tipCents: p.tipCents,
    method: p.method,
    checkNumber: p.checkNumber,
    status: "succeeded",
    receivedAt: p.receivedAt,
    recordedBy: p.recordedBy,
    note: p.note,
  };
};
function invoiceDetail(inv: Inv): InvoiceDetail {
  const c = clientOf(inv);
  return {
    ...invoiceRow(inv),
    poNumber: inv.poNumber,
    nobodyWorked: false,
    isBusiness: isBusiness({ companyName: c.companyName }),
    noteToClient: inv.noteToClient,
    payNote: inv.payNote,
    debt: inv.debt,
    remindersOff: inv.remindersOff,
    followUps: [],
    checkMailedAt: inv.checkMailedAt ?? null,
    firstSentAt: inv.firstSentAt,
    sentVia: inv.sentVia,
    sentTo: inv.sentTo,
    payLink: appLink("pay", inv.token),
    lines: inv.lines,
    visits: inv.visits,
    crew: inv.visits.length ? [] : inv.crew,
    payments: payments.filter((p) => p.invoiceId === inv.id).map(paymentRow),
    edits: [...inv.edits].reverse(),
    client: {
      id: c.id,
      name: c.name,
      firstName: c.firstName,
      phone: c.phone,
      email: c.email,
      address: c.address,
      creditCents: credits.get(c.id) ?? 0,
      card: cards.get(c.id) ?? null,
      autoCharge: autoCharge.get(c.id) ?? false,
    },
    crewCollected: inv.crewCollected,
    payWays: { ...(inv.payWays ?? { card: true, bank: true, klarna: false }), own: Boolean(inv.payWays) },
    // The preview has no job frequency here: repeating plans carry it in their name.
    klarnaFits:
      !/weekly|monthly/i.test(inv.jobName ?? "") &&
      totalsOf(inv).balanceCents >= KLARNA_MIN_CENTS &&
      !(autoCharge.get(c.id) ?? false),
    // Seeded invoices are made on load, after their made-up send day.
    createdAt: inv.firstSentAt && inv.firstSentAt < inv.createdAt ? inv.firstSentAt : inv.createdAt,
    viewedAt: invoiceRow(inv).viewed ? inv.sentAt : null,
    paidAt:
      inv.status === "paid"
        ? (payments
            .filter((p) => p.invoiceId === inv.id)
            .map((p) => p.receivedAt)
            .sort()
            .pop() ?? null)
        : null,
    mergeWith:
      inv.status === "to_check" || inv.status === "sent"
        ? invoices
            .filter(
              (o) =>
                o.id !== inv.id &&
                o.companyId === inv.companyId &&
                o.clientId === inv.clientId &&
                (o.status === "to_check"
                  ? inv.status === "to_check" || invoiceRow(o).paidCents === 0
                  : inv.status === "to_check" && o.status === "sent" && invoiceRow(o).balanceCents > 0),
            )
            .map((o) => {
              const r = invoiceRow(o);
              return {
                id: r.id,
                number: r.number,
                status: o.status,
                totalCents: r.totalCents,
                firstServiceDate: r.firstServiceDate,
              };
            })
        : [],
    autoChargeAt: null,
    autoChargeHeld: false,
  };
}

function newInvoice(companyId: string, clientId: string, jobName: string | null): Inv {
  const inv: Inv = {
    companyId,
    id: newId(),
    number: null,
    clientId,
    status: "to_check",
    token: token(),
    lines: [],
    visits: [],
    jobName,
    tipCents: 0,
    poNumber: null,
    noteToClient: null,
    payNote: null,
    debt: false,
    remindersOff: false,
    firstSentAt: null,
    sentAt: null,
    sentVia: null,
    sentTo: null,
    issuedOn: null,
    dueAt: null,
    crewCollected: null,
    crew: [],
    edits: [],
    createdAt: new Date().toISOString(),
  };
  invoices.push(inv);
  return inv;
}

/** Who worked an invoice typed by hand, from person ids; null when someone is not on the team. */
function crewChips(ctx: MoneyCtx, ids: readonly string[]): { id: string; name: string }[] | null {
  const out: { id: string; name: string }[] = [];
  for (const id of new Set(ids)) {
    const p = ctx.people.find((x) => x.id === id);
    if (!p) return null;
    out.push({ id, name: shortName(p.firstName, p.lastName) });
  }
  return out;
}

/**
 * Invoices typed by hand, for the preview's payroll (question 43): paid ones as paid work on the day their last payment
 * came in, unpaid ones as waiting. Keyed by the invoice id, like the real server.
 */
export function handWork(companyId: string) {
  const dayOf = (at: string) => new Intl.DateTimeFormat("en-CA", { timeZone: "America/Chicago" }).format(new Date(at));
  const paid: (PaidWork & { companyId: string })[] = [];
  const waiting: (WaitingWork & { companyId: string })[] = [];
  for (const inv of invoices) {
    if (inv.companyId !== companyId || inv.visits.length || inv.status === "void" || inv.status === "no_charge") continue;
    const base = inv.lines.reduce((s, l) => s + l.amountCents, 0);
    const head = {
      companyId,
      visitId: inv.id,
      date: inv.issuedOn ?? dayOf(inv.createdAt),
      clientName: clientOf(inv).name,
      crew: inv.crew.map((c) => c.id),
      poolPercent: null,
      byHand: true,
    };
    const last = payments.filter((p) => p.invoiceId === inv.id).at(-1);
    if (inv.status === "paid" && last)
      paid.push({ ...head, on: dayOf(last.receivedAt), baseCents: base, tipCents: inv.tipCents, crewCollected: false });
    else waiting.push({ ...head, baseCents: base });
  }
  return { paid, waiting };
}
function addVisit(inv: Inv, v: DoneVisit): void {
  inv.visits.push({ ref: v.visitId, date: v.date, jobName: v.jobName });
  for (const l of v.lines)
    inv.lines.push({
      id: newId(),
      visitId: v.visitId,
      description: l.description,
      note: null,
      quantity: l.quantity,
      unitPriceCents: l.unitPriceCents,
      amountCents: l.amountCents,
      serviceDate: v.date,
      propertyLabel: null,
    });
}
/** A visit marked done: onto the client's To check invoice from the same day, or a new one. Month end and monthly wait. */
function billVisit(v: DoneVisit): void {
  if (v.billing !== "visit") return;
  if (invoices.some((i) => i.visits.some((x) => x.ref === v.visitId))) return;
  const open = invoices.find(
    (i) =>
      i.companyId === v.companyId &&
      i.clientId === v.clientId &&
      i.status === "to_check" &&
      i.visits.some((x) => x.date === v.date),
  );
  const inv = open ?? newInvoice(v.companyId, v.clientId, v.jobName);
  addVisit(inv, v);
  if (v.crewCollected) {
    inv.crewCollected = v.crewCollected;
    inv.payNote =
      v.crewCollected === "cash"
        ? "The crew kept the client's cash. It is saved as paid by the crew and comes off their pay."
        : v.crewCollected === "company"
          ? "The crew says the client gave a company check. Record it when you get the check."
          : `The crew says the client paid by ${v.crewCollected}. Check it and record the payment.`;
  }
}
function unbillVisit(visitId: string): void {
  const inv = invoices.find((i) => i.status === "to_check" && i.visits.some((x) => x.ref === visitId));
  if (!inv) return;
  inv.visits = inv.visits.filter((x) => x.ref !== visitId);
  inv.lines = inv.lines.filter((l) => l.visitId !== visitId);
  if (!inv.visits.length && !inv.lines.length) invoices.splice(invoices.indexOf(inv), 1);
}

function giveNumber(inv: Inv): void {
  inv.number ??= nextNumber(inv.companyId, "invoice");
}
function markSent(inv: Inv, brand: MoneyBrand, via: string | null, to: string | null): void {
  giveNumber(inv);
  const now = new Date();
  if (!inv.firstSentAt) {
    inv.firstSentAt = now.toISOString();
    inv.issuedOn = TODAY;
    const business = isBusiness({ companyName: clientOf(inv).companyName });
    inv.dueAt = pastDueAt(now, business, null, brand).toISOString();
    // Account credit is used the first time the invoice goes out.
    const credit = credits.get(inv.clientId) ?? 0;
    const balance = totalsOf(inv).balanceCents;
    const use = Math.min(credit, balance);
    if (use > 0) {
      credits.set(inv.clientId, credit - use);
      payments.push({
        companyId: inv.companyId,
        id: newId(),
        invoiceId: inv.id,
        clientId: inv.clientId,
        amountCents: use,
        tipCents: 0,
        method: "credit",
        checkNumber: null,
        receivedAt: now.toISOString(),
        recordedBy: null,
        note: "Account credit used",
      });
    }
  }
  inv.sentAt = now.toISOString();
  inv.sentVia = via;
  inv.sentTo = to;
  if (inv.status === "to_check" || inv.status === "to_collect") inv.status = "sent";
  settle(inv);
}
function previewOf(inv: Inv, brand: MoneyBrand, via: "text" | "email"): SendPreview {
  const c = clientOf(inv);
  const choices = [via === "text" ? c.phone : c.email].filter((x): x is string => Boolean(x));
  const to = choices[0] ?? null;
  const body = invoiceMessage(
    brand.invoiceText,
    { brand: brand.name, first: c.firstName, job: (inv.jobName ?? "cleaning").toLowerCase() },
    appLink("pay", inv.token),
  );
  const others = to ? workLookup.sharing(inv.companyId, inv.clientId, to) : [];
  return {
    via,
    to,
    choices,
    subject: via === "email" ? brand.invoiceSubject.replace("{brand}", brand.name) : null,
    body,
    warnings: others.map(
      (n) => `This ${via === "text" ? "number" : "email"} also belongs to ${n}. Check it is the right person.`,
    ),
    pdf: false,
  };
}
function pay(
  inv: Inv,
  amountCents: number,
  tipCents: number,
  method: PaymentMethod,
  by: string | null,
  note: string | null = null,
  checkNumber: string | null = null,
): void {
  if (tipCents) inv.tipCents += tipCents;
  payments.push({
    companyId: inv.companyId,
    id: newId(),
    invoiceId: inv.id,
    clientId: inv.clientId,
    amountCents: amountCents + tipCents,
    tipCents,
    method,
    checkNumber,
    receivedAt: new Date().toISOString(),
    recordedBy: by,
    note,
  });
  settle(inv);
}

// Quotes ------------------------------------------------------------------------------------------------------------

const quoteName = (q: Quote) => (q.clientId ? (workLookup.client(q.clientId)?.name ?? "Client") : (q.contactName ?? "Client"));
const quoteSums = (q: Quote) => quoteTotals(q.lines, q.deposit);
const quoteStatus = (q: Quote): QuoteStatus => (q.status === "sent" && q.expiresOn && q.expiresOn < TODAY ? "expired" : q.status);
function quoteRow(q: Quote): QuoteRow {
  return {
    id: q.id,
    number: q.number,
    name: quoteName(q),
    clientId: q.clientId,
    title: q.title,
    status: quoteStatus(q),
    totalCents: quoteSums(q).totalCents,
    sentAt: q.sentAt,
    approvedAt: q.approvedAt,
    expiresOn: q.expiresOn,
    createdAt: q.createdAt,
    address: q.address ?? (q.clientId ? (workLookup.client(q.clientId)?.address ?? null) : null),
    viewed: q.status !== "draft",
  };
}
function quoteDetail(q: Quote): QuoteDetail {
  // As the server does: a quote for a client reaches them at the client's phone and email.
  const client = q.clientId ? workLookup.client(q.clientId) : null;
  return {
    ...quoteRow(q),
    leadId: q.leadId,
    contactName: q.contactName,
    contactPhone: q.contactPhone ?? client?.phone ?? null,
    contactEmail: q.contactEmail ?? client?.email ?? null,
    address: q.address ?? client?.address ?? null,
    message: q.message,
    deposit: q.deposit,
    depositCents: quoteSums(q).depositCents,
    frequency: q.frequency,
    lines: q.lines.map((l) => ({ ...l, optionGroup: null })),
    signature: q.signature,
    followUps: [],
    jobId: q.jobId,
    depositInvoiceId: q.depositInvoiceId,
    link: appLink("quote", q.token),
    brand: "The Cleaners",
    terms: q.terms ?? null,
    cardRequired: false,
    views: 2,
    lastViewedAt: new Date().toISOString(),
    proposal: proposalStore.has(q.id),
    sections: q.sections ?? [],
    sectionFiles: filesOfSections(q.sections ?? []),
  };
}
const qLines = (input: z.infer<typeof quoteInput>["lines"]): QLine[] =>
  input.map((l) => ({
    id: newId(),
    description: l.description,
    note: l.note ?? null,
    quantity: l.quantity,
    unitPriceCents: l.unitPriceCents,
    amountCents: Math.round(l.quantity * l.unitPriceCents),
    optional: l.optional,
    picked: l.optional ? l.picked : true,
    recurring: l.recurring,
    bill: l.bill,
  }));
/** The agency (draft 13): its deal stages, and its proposals carry the 3 month agreement. */
const agencyCompanies = new Set<string>();
export function useAgencyStages(companyId: string): void {
  agencyCompanies.add(companyId);
  if (stages.some((s) => s.companyId === companyId)) return;
  AGENCY_STAGES.forEach((s, position) => stages.push({ companyId, id: newId(), name: s.name, position, fixed: s.fixed }));
}
/** The agency's deals and quotes for its own preview screens. */
export function agencyLeads(companyId: string) {
  return {
    stages: stagesOf(companyId),
    leads: leads.filter((l) => l.companyId === companyId),
    quotes: quotes.filter((q) => q.companyId === companyId),
  };
}
export function addAgencyLead(
  lead: Omit<Lead, "id" | "stageChangedAt" | "createdAt" | "stageId"> & { stage: string; daysAgo: number },
): string {
  const at = new Date(Date.now() - lead.daysAgo * 86_400_000).toISOString();
  const { stage, daysAgo: _d, ...rest } = lead;
  const id = newId();
  leads.push({
    ...rest,
    id,
    stageId: stagesOf(lead.companyId).find((s) => s.name === stage)!.id,
    stageChangedAt: at,
    createdAt: at,
  });
  return id;
}

function stagesOf(companyId: string): Stage[] {
  if (!stages.some((s) => s.companyId === companyId))
    (
      [
        ["New", null],
        ["Contacted", null],
        ["Quote sent", null],
        ["Won", "won"],
        ["Lost", "lost"],
      ] as const
    ).forEach(([name, fixed], position) => stages.push({ companyId, id: newId(), name, position, fixed }));
  return stages.filter((s) => s.companyId === companyId).sort((a, b) => a.position - b.position);
}
const stageNamed = (companyId: string, name: string) => stagesOf(companyId).find((s) => s.name === name);
function moveLead(leadId: string | null, stage: Stage | undefined, extra: Partial<Lead> = {}): void {
  const lead = leads.find((l) => l.id === leadId);
  if (!lead || !stage) return;
  if (lead.stageId !== stage.id) lead.stageChangedAt = new Date().toISOString();
  lead.stageId = stage.id;
  Object.assign(lead, extra);
}
const leadRow = (l: Lead): LeadRow => ({
  id: l.id,
  name: l.name,
  phone: l.phone,
  email: l.email,
  address: l.address,
  place: { ...splitAddress(l.address) },
  service: l.service,
  valueCents: l.valueCents,
  source: l.source,
  stageId: l.stageId,
  clientId: l.clientId,
  quoteId: l.quoteId,
  note: l.note,
  businessName: l.businessName ?? null,
  website: l.website ?? null,
  assignedTo: l.assignedTo ?? null,
  stageChangedAt: l.stageChangedAt,
  createdAt: l.createdAt,
});

// Public pages ------------------------------------------------------------------------------------------------------

function publicInvoice(inv: Inv, brand: MoneyBrand): PublicInvoice {
  const t = totalsOf(inv);
  const open = inv.status !== "paid" && inv.status !== "void" && inv.status !== "no_charge";
  const card = cards.get(inv.clientId);
  return {
    brand: {
      name: brand.name,
      color: brand.color,
      phone: brand.phone,
      email: brand.email,
      links: [],
      look: lookFor("cleaning", brand.logoWhiteUrl ?? brand.logoUrl, {
        accent: brand.accentColor,
        menu: brand.secondaryColor,
        cta: brand.ctaColor,
      }),
      address: null,
      website: null,
    },
    number: inv.number,
    firstName: clientOf(inv).firstName,
    client: { name: clientOf(inv).name, address: null, phone: null },
    jobName: null,
    home: null,
    issuedOn: inv.issuedOn,
    dueAt: null,
    publishableKey: null,
    status: inv.status === "paid" ? "paid" : inv.status === "void" || inv.status === "no_charge" ? "void" : "open",
    lines: inv.lines.map((l) => ({
      description: l.description,
      note: l.note,
      link: null,
      quantity: l.quantity,
      amountCents: l.amountCents,
      serviceDate: l.serviceDate,
    })),
    poNumber: inv.poNumber,
    noteToClient: inv.noteToClient,
    subtotalCents: t.subtotalCents,
    tipCents: inv.tipCents,
    totalCents: t.totalCents,
    paidCents: t.paidCents,
    balanceCents: t.balanceCents,
    tipChoices: open && tipsOn(brand.trade, brand.tips) ? tipChoices(t.subtotalCents) : [],
    tipUntil: null,
    maxTipCents: maxTip(t.subtotalCents),
    canPayCard: open && brand.stripe,
    payWays: open ? ["card"] : [],
    bankOnTheWayCents: 0,
    saveCardSentence: saveCardSentence(brand.name),
    cardOnFile: card ? { brand: card.brand, last4: card.last4 } : null,
    autoCharge: brand.trade === "lawn" ? "ask" : "offer",
    photos: [],
    payNote: "Cash or check is fine too. Make checks out to the business.",
    footer: "",
  };
}
/** An agency plan's agreement on the proposal page (a picked monthly line), as on the server. */
function agreementOf(q: Quote, brand: string): PublicQuote["agreement"] {
  const plan = agreementSplit(
    q.lines.map((l) => ({ amountCents: l.amountCents, bill: l.bill, inPlan: !l.optional || l.picked })),
  );
  if (!agencyCompanies.has(q.companyId) || plan.monthlyCents <= 0) return null;
  return {
    words: agreementWords({ ...plan, brand }),
    autoPayWords: autoPayWords({ monthlyCents: plan.monthlyCents, brand }),
    monthlyCents: plan.monthlyCents,
    setupCents: plan.setupCents,
    autoPay: q.signature ? true : null,
  };
}

function publicQuote(q: Quote, brand: MoneyBrand): PublicQuote {
  const dep = invoices.find((i) => i.id === q.depositInvoiceId);
  const sections = filledSections(q.sections ?? []);
  return {
    policies: q.signature ? [] : policiesToSign(SETTINGS_DEFAULTS),
    sections,
    sectionFiles: filesOfSections(sections),
    brand: {
      name: brand.name,
      color: brand.color,
      phone: brand.phone,
      email: brand.email,
      links: [],
      look: lookFor("cleaning", brand.logoWhiteUrl ?? brand.logoUrl, {
        accent: brand.accentColor,
        menu: brand.secondaryColor,
        cta: brand.ctaColor,
      }),
      address: null,
      website: null,
    },
    number: q.number,
    firstName: (q.contactName ?? quoteName(q)).split(/\s+/)[0] ?? "there",
    account: q.clientId !== null,
    title: q.title,
    home: null,
    message: q.message,
    status: quoteStatus(q),
    expiresOn: q.expiresOn,
    lines: q.lines.map((l) => ({
      id: l.id,
      description: l.description,
      note: l.note,
      link: null,
      quantity: l.quantity,
      amountCents: l.amountCents,
      optional: l.optional,
      picked: l.picked,
      recurring: l.recurring,
      optionGroup: null,
      bill: l.bill,
    })),
    proposal: proposalPage(q, brand),
    agreement: agreementOf(q, brand.name),
    deposit: q.deposit,
    signature: q.signature,
    payLink: dep && totalsOf(dep).balanceCents > 0 ? `/pay/${dep.token}` : null,
    terms: q.terms ?? null,
    cardRequired: false,
    signRequired: true,
    hasCard: false,
    saveCardSentence: "",
    answers: null,
    services: [],
    extras: [],
    auto: null,
    booking: null,
    bookedFor: null,
  };
}

/** The client's pay and quote pages. They need no sign in; the long token in the link is the key. */
export function moneyPublicRoute(
  method: string,
  p: string,
  body: unknown,
  brandOf: (companyId: string) => MoneyBrand,
): Answer | null {
  const seg = p.split("/");
  if (seg[0] !== "public" || (seg[1] !== "pay" && seg[1] !== "quote")) return null;
  // Preview links /i/demo and /q/demo open the first sent invoice and quote, to look at the client pages.
  const t = seg[2] ?? "";
  if (seg[1] === "pay") {
    const inv = invoices.find((i) =>
      t === "demo" ? i.status === "sent" && totalsOf(i).balanceCents > 0 : i.token === t && i.status !== "to_check",
    );
    if (!inv) return NOT_FOUND("link");
    const brand = brandOf(inv.companyId);
    if (method === "GET" && seg.length === 3) return ok(publicInvoice(inv, brand));
    if (method === "POST" && seg[3] === "start") {
      const parsed = payStartBody.safeParse(body);
      if (!parsed.success) return bad(parsed.error);
      if (inv.status === "paid") return fail(409, "invoice_paid", "This invoice is already paid. Thank you.");
      if (!brand.stripe) return fail(409, "stripe_not_set", "Card pay is not set up yet. Call us to pay.");
      const sub = totalsOf(inv).subtotalCents;
      if (parsed.data.tipCents > 0 && !tipsOn(brand.trade, brand.tips))
        return fail(422, "no_tips", "Tips are off for this invoice.");
      if (parsed.data.tipCents > maxTip(sub))
        return fail(422, "tip_too_big", "That tip is more than we can take. Pick a smaller amount.");
      const session = `pi_preview_${String(sessions.size + 1)}`;
      sessions.set(session, { invoiceId: inv.id, tipCents: parsed.data.tipCents, saveCard: parsed.data.saveCard });
      return ok({ intentId: session, clientSecret: `${session}_secret_preview`, publishableKey: null, fake: true });
    }
    if (method === "POST" && (seg[3] === "done" || seg[3] === "test-pay")) {
      const parsed = payDoneBody.safeParse(body);
      if (!parsed.success) return bad(parsed.error);
      const s = sessions.get(parsed.data.paymentIntent);
      if (s?.invoiceId !== inv.id) return ok({ paid: inv.status === "paid" });
      sessions.delete(parsed.data.paymentIntent);
      if (inv.status !== "paid") {
        pay(inv, totalsOf(inv).balanceCents, s.tipCents, "card", null);
        if (s.saveCard) cards.set(inv.clientId, { brand: "Visa", last4: "4242", expMonth: 12, expYear: 2030 });
      }
      return ok({ paid: true });
    }
    if (method === "GET" && seg[3] === "pdf")
      return fail(501, "not_built", "PDFs come from the server. The preview has no server.");
    return null;
  }
  const q = quotes.find((x) => (t === "demo" ? x.status === "sent" : x.token === t && x.status !== "draft"));
  if (!q) return NOT_FOUND("link");
  const brand = brandOf(q.companyId);
  if (method === "GET" && seg.length === 3) return ok(publicQuote(q, brand));
  if (method === "POST" && seg[3] === "approve") {
    const parsed = quoteApproveBody.safeParse(body);
    if (!parsed.success) return bad(parsed.error);
    if (q.status === "approved" || q.status === "converted") return ok(publicQuote(q, brand));
    if (quoteStatus(q) === "expired") return fail(409, "quote_expired", "This quote has expired. Ask us for a new one.");
    if (q.status !== "sent") return fail(409, "quote_closed", "This quote is closed.");
    for (const l of q.lines) if (l.optional) l.picked = parsed.data.picked.includes(l.id);
    const at = new Date().toISOString();
    q.status = "approved";
    q.approvedAt = at;
    q.signature = { name: parsed.data.signature, at, drawing: parsed.data.drawing };
    q.clientId ??= workLookup.addClient(q.companyId, q.contactName ?? parsed.data.signature, q.contactPhone, q.contactEmail).id;
    moveLead(q.leadId, stageNamed(q.companyId, "Won"), { clientId: q.clientId });
    const { depositCents } = quoteSums(q);
    if (depositCents > 0) {
      const inv = newInvoice(q.companyId, q.clientId, q.title);
      inv.lines.push({
        id: newId(),
        visitId: null,
        description: `Deposit for quote #${String(q.number)}`,
        note: null,
        quantity: 1,
        unitPriceCents: depositCents,
        amountCents: depositCents,
        serviceDate: null,
        propertyLabel: null,
      });
      markSent(inv, brand, null, null);
      q.depositInvoiceId = inv.id;
    }
    return ok(publicQuote(q, brand));
  }
  return null;
}

// Office routes -----------------------------------------------------------------------------------------------------

export function moneyRoute(ctx: MoneyCtx, method: string, p: string, query: URLSearchParams, b: unknown): Answer | null {
  const cid = ctx.companyId;
  const seg = p.split("/");
  const route = `${method} ${p}`;
  const { gate } = ctx;
  const mine = (id: string | undefined) => invoices.find((i) => i.id === id && i.companyId === cid);

  if (route === "GET activity-feed") {
    // The bell's feed from the preview's own requests, quotes, invoices and payments, all on one page.
    const asked = new Set((query.get("kinds") ?? FEED_KINDS.join(",")).split(","));
    const on = (k: FeedKind) => asked.has(k) && gate(FEED_ACCESS[k]);
    const nameOf = (clientId: string | null, other: string | null = null) =>
      (clientId ? workLookup.client(clientId)?.name : null) ?? other ?? "Client";
    const items: FeedItem[] = [];
    const add = (i: Omit<FeedItem, "quote"> & { quote?: string | null }) => items.push({ quote: null, ...i });
    if (on("requests"))
      for (const l of leads.filter((x) => x.companyId === cid))
        add({
          id: `request:${l.id}`,
          kind: "requests",
          at: l.createdAt,
          title: l.source ? `New request from ${l.source}` : "New request",
          detail: l.service,
          client: l.name,
          link: `/requests/${l.id}`,
        });
    if (on("quotes"))
      for (const q of quotes.filter((x) => x.companyId === cid)) {
        const base = {
          kind: "quotes" as const,
          detail: `Quote #${String(q.number)}${q.title ? ` - ${q.title}` : ""}`,
          client: nameOf(q.clientId, q.contactName),
          link: `/quotes/${q.id}`,
        };
        const price = formatMoney(quoteSums(q).totalCents as Cents);
        add({ ...base, id: `quote-made:${q.id}`, at: q.createdAt, title: `Quote created - ${price}` });
        if (q.sentAt) add({ ...base, id: `quote-sent:${q.id}`, at: q.sentAt, title: `Quote sent - ${price}` });
        if (q.approvedAt)
          add({
            ...base,
            id: `quote-approved:${q.id}`,
            at: q.approvedAt,
            title: `${q.signature?.name ? `${q.signature.name} approved a quote` : "Quote approved"} - ${price}`,
          });
      }
    if (on("invoices"))
      for (const i of invoices.filter((x) => x.companyId === cid)) {
        const base = {
          kind: "invoices" as const,
          detail: `Invoice${i.number ? ` #${String(i.number)}` : ""}${i.jobName ? ` - ${i.jobName}` : ""}`,
          client: nameOf(i.clientId),
          link: `/invoices/${i.id}`,
        };
        const total = formatMoney(totalsOf(i).totalCents as Cents);
        add({ ...base, id: `invoice-made:${i.id}`, at: i.createdAt, title: `Invoice created - ${total}` });
        if (i.firstSentAt) add({ ...base, id: `invoice-sent:${i.id}`, at: i.firstSentAt, title: `Invoice sent - ${total}` });
      }
    if (on("payments"))
      for (const p of payments.filter((x) => x.companyId === cid)) {
        const inv = invoices.find((i) => i.id === p.invoiceId);
        add({
          id: `payment:${p.id}`,
          kind: "payments",
          at: p.receivedAt,
          title: `Payment received - ${formatMoney(p.amountCents as Cents)} by ${p.method}`,
          detail: inv ? `Invoice${inv.number ? ` #${String(inv.number)}` : ""}` : null,
          client: nameOf(p.clientId),
          link: inv ? `/invoices/${inv.id}` : `/clients/${p.clientId}`,
        });
      }
    items.sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
    return ok({ items: items.slice(0, 100), next: null });
  }

  if (route === "GET home/money") {
    if (!gate("invoice.read")) return NOT_ALLOWED();
    const list = invoices.filter((i) => i.companyId === cid);
    const by = (s: InvoiceState) => list.filter((i) => stateOf(i) === s);
    const owed = (l: typeof list) => l.reduce((s, i) => s + totalsOf(i).balanceCents, 0);
    const qs = (st: QuoteStatus) => quotes.filter((q) => q.companyId === cid && quoteStatus(q) === st);
    const worth = (l: Quote[]) => l.reduce((s, q) => s + quoteSums(q).totalCents, 0);
    const answer: MoneyToDo = {
      toCheck: by("to_check").length,
      toCheckCents: by("to_check").reduce((s, i) => s + totalsOf(i).totalCents, 0),
      awaiting: by("awaiting").length,
      awaitingCents: owed(by("awaiting")) + owed(by("past_due")),
      pastDue: by("past_due").length,
      pastDueCents: owed(by("past_due")),
      toCollect: list.filter((i) => i.status === "to_collect").length,
      quotesDraft: qs("draft").length,
      quotesWaiting: qs("sent").length,
      quotesWaitingCents: worth(qs("sent")),
      quotesApproved: qs("approved").length,
      quotesApprovedCents: worth(qs("approved")),
    };
    return ok(answer);
  }

  if (route === "GET invoices/to-check-count") {
    if (!gate("invoice.read")) return NOT_ALLOWED();
    return ok({ count: invoices.filter((i) => i.companyId === cid && i.status === "to_check").length });
  }
  if (route === "GET invoices") {
    if (!gate("invoice.read")) return NOT_ALLOWED();
    const state = query.get("state") ?? "all";
    const q = (query.get("q") ?? "").trim().toLowerCase();
    const clientId = query.get("clientId");
    const all = invoices.filter((i) => i.companyId === cid && (!clientId || i.clientId === clientId));
    const from = query.get("from");
    const to = query.get("to");
    const issued = (r: InvoiceRow) => r.sentAt?.slice(0, 10) ?? r.firstServiceDate ?? "";
    const counts = Object.fromEntries(INVOICE_STATES.map((s) => [s, all.filter((i) => stateOf(i) === s).length]));
    // The main All list: what needs money first, closed ones archived and debts kept off (the server's priorityOrder).
    const main = state === "all" && !clientId && !q;
    const rows = all
      .filter((i) =>
        main
          ? i.status !== "void" && i.status !== "no_charge" && !i.debt
          : state === "all" || stateOf(i) === state || (state === "to_collect" && i.status === "to_collect"),
      )
      .map(invoiceRow)
      .filter(
        (r) =>
          (!q ||
            r.clientName.toLowerCase().includes(q) ||
            String(r.number ?? "").includes(q) ||
            r.subject.toLowerCase().includes(q)) &&
          (!from || issued(r) >= from) &&
          (!to || issued(r) <= to),
      )
      .sort(
        (a, b) =>
          (main ? invoiceRank(a) - invoiceRank(b) : 0) ||
          (b.sentAt ?? b.firstServiceDate ?? "").localeCompare(a.sentAt ?? a.firstServiceDate ?? ""),
      );
    const open = all.filter((i) => i.status === "sent" || i.status === "to_collect");
    const owed = open.filter((i) => !i.debt && totalsOf(i).balanceCents > 0);
    const since = new Date(Date.parse(`${TODAY}T12:00:00Z`) - 30 * 86_400_000).toISOString();
    const issued30 = all.filter((i) => i.sentAt !== null && i.sentAt >= since && i.status !== "void");
    const since60 = new Date(Date.parse(`${TODAY}T12:00:00Z`) - 60 * 86_400_000).toISOString();
    const issued60 = all.filter((i) => i.sentAt !== null && i.sentAt >= since60 && i.sentAt < since && i.status !== "void");
    const sumOf = (list: Inv[], by: (i: Inv) => number) => list.reduce((s, i) => s + by(i), 0);
    const summary: InvoiceSummary = {
      toCheck: counts.to_check ?? 0,
      monthEnd: 0,
      toCollect: all.filter((i) => i.status === "to_collect").length,
      awaiting: counts.awaiting ?? 0,
      onTheWay: 0,
      pastDue: counts.past_due ?? 0,
      owedCount: owed.length,
      owedCents: owed.reduce((s, i) => s + totalsOf(i).balanceCents, 0),
      issuedCount: issued30.length,
      issuedCents: issued30.reduce((s, i) => s + totalsOf(i).totalCents - i.tipCents, 0),
      toCheckCents: sumOf(
        all.filter((i) => stateOf(i) === "to_check"),
        (i) => totalsOf(i).totalCents,
      ),
      toCollectCents: sumOf(
        all.filter((i) => i.status === "to_collect"),
        (i) => totalsOf(i).balanceCents,
      ),
      awaitingCents: sumOf(
        all.filter((i) => stateOf(i) === "awaiting"),
        (i) => totalsOf(i).balanceCents,
      ),
      onTheWayCents: 0,
      pastDueCents: sumOf(
        all.filter((i) => stateOf(i) === "past_due"),
        (i) => totalsOf(i).balanceCents,
      ),
      issuedBeforeCount: issued60.length,
      issuedBeforeCents: sumOf(issued60, (i) => totalsOf(i).totalCents - i.tipCents),
      // Made up for the preview: about two days from sent to paid, a little slower the month before.
      payDays: issued30.length ? 2.4 : null,
      payDaysBefore: issued60.length ? 3.1 : null,
    };
    return ok({ invoices: rows, counts, summary, nextCursor: null });
  }
  if (route === "POST invoices") {
    if (!gate("invoice.update")) return NOT_ALLOWED();
    const parsed = invoiceNewBody.safeParse(b);
    if (!parsed.success) return bad(parsed.error);
    if (!workLookup.client(parsed.data.clientId)) return NOT_FOUND("client");
    const inv = newInvoice(cid, parsed.data.clientId, null);
    inv.lines = parsed.data.lines.map((l) => ({
      id: newId(),
      visitId: null,
      description: l.description,
      note: l.note ?? null,
      quantity: l.quantity,
      unitPriceCents: l.unitPriceCents,
      amountCents: Math.round(l.quantity * l.unitPriceCents),
      serviceDate: l.serviceDate ?? null,
      propertyLabel: null,
    }));
    inv.noteToClient = parsed.data.noteToClient ?? null;
    const crew = crewChips(ctx, parsed.data.crew ?? []);
    if (!crew) return fail(422, "not_on_team", "Pick people from your team list.");
    inv.crew = crew;
    ctx.log("invoice.create");
    return ok(invoiceDetail(inv), 201);
  }
  if (route === "GET payments") {
    if (!gate("invoice.read")) return NOT_ALLOWED();
    const dayOf = (at: string) => new Intl.DateTimeFormat("en-CA", { timeZone: ctx.brand.timezone }).format(new Date(at));
    const from = query.get("from");
    const to = query.get("to");
    const how = query.get("method");
    const status = query.get("status");
    const clientId = query.get("clientId");
    const q = (query.get("q") ?? "").trim().toLowerCase().replace("#", "");
    const mineAll = payments.filter((x) => x.companyId === cid).map(listRow);
    const rows = mineAll
      .filter(
        (r) =>
          (!from || dayOf(r.receivedAt) >= from) &&
          (!to || dayOf(r.receivedAt) <= to) &&
          (!clientId || r.clientId === clientId) &&
          (!how || r.method === how) &&
          (!status || r.status === status) &&
          (!q || r.clientName.toLowerCase().includes(q) || String(r.invoiceNumber ?? "") === q || r.checkNumber === q),
      )
      .sort((a, b) => b.receivedAt.localeCompare(a.receivedAt));
    const month = TODAY.slice(0, 7);
    const thisMonth = mineAll.filter((r) => dayOf(r.receivedAt).slice(0, 7) === month);
    const owed = invoices.filter((i) => i.companyId === cid && ["awaiting", "past_due"].includes(stateOf(i)));
    const answer: PaymentList = {
      payments: rows,
      count: rows.length,
      totalCents: rows.reduce((s, r) => s + r.amountCents - r.tipCents, 0),
      tipCents: rows.reduce((s, r) => s + r.tipCents, 0),
      summary: {
        monthCents: thisMonth.reduce((s, r) => s + r.amountCents - r.tipCents, 0),
        monthCount: thisMonth.filter((r) => r.amountCents > 0).length,
        monthTipCents: thisMonth.reduce((s, r) => s + r.tipCents, 0),
        payDays: 1.4,
        payDaysBefore: 2.1,
        onTimePct: 79,
        onTimePctBefore: 74,
        owedCents: owed.reduce((s, i) => s + totalsOf(i).balanceCents, 0),
        owedCount: owed.length,
      },
    };
    return ok(answer);
  }
  if (seg[0] === "payments" && seg[1] && method === "GET") {
    if (!gate("invoice.read")) return NOT_ALLOWED();
    const p = payments.find((x) => x.companyId === cid && x.id === seg[1]);
    if (!p) return NOT_FOUND("payment");
    const row = listRow(p);
    const c = workLookup.client(p.clientId);
    const answer: PaymentDetail = {
      ...row,
      client: {
        id: p.clientId,
        name: row.clientName,
        phone: c?.phone ?? null,
        email: c?.email ?? null,
        address: c?.address ?? null,
      },
      visitDate: null,
      transaction: p.method === "card" ? `ch_demo${p.id.slice(0, 8)}` : null,
      refundOf: null,
      timeline: [{ at: p.receivedAt, what: "Payment collected", amountCents: null }],
    };
    return ok(answer);
  }

  if (route === "POST invoices/merge") {
    if (!gate("invoice.update")) return NOT_ALLOWED();
    const parsed = mergeInvoicesBody.safeParse(b);
    if (!parsed.success) return bad(parsed.error);
    const list = parsed.data.ids.map((id) => mine(id));
    if (list.some((x) => !x)) return NOT_FOUND("invoice");
    const all = (list as Inv[]).sort((x, y) => x.createdAt.localeCompare(y.createdAt));
    const check = mergeCheck(
      all.map((x) => ({
        id: x.id,
        clientId: x.clientId,
        status: x.status,
        paidCents: invoiceRow(x).paidCents,
        billKind: null,
        quoteId: null,
      })),
      gate("invoice.edit_paid"),
    );
    if (!check.ok) return fail(check.status, check.code, check.message);
    const keep = all.find((x) => x.id === check.keepId)!;
    const rest = all.filter((x) => x.id !== keep.id);
    for (const o of rest) {
      keep.lines.push(...o.lines);
      keep.visits.push(...o.visits);
      o.lines = [];
      o.status = "void";
      o.payNote = "Merged into another invoice";
    }
    if (new Set(all.map((x) => x.jobName)).size > 1) keep.jobName = null;
    ctx.log("invoice.merge");
    return ok(invoiceDetail(keep));
  }
  if (seg[0] === "invoices" && seg[1]) {
    const inv = mine(seg[1]);
    if (!inv) return NOT_FOUND("invoice");
    const sub = seg[2];
    if (!sub && method === "GET") return gate("invoice.read") ? ok(invoiceDetail(inv)) : NOT_ALLOWED();
    if (sub === "take-off" && method === "POST") {
      if (!gate("invoice.update")) return NOT_ALLOWED();
      const parsed = takeVisitOffBody.safeParse(b);
      if (!parsed.success) return bad(parsed.error);
      const visit = inv.visits.find((v) => v.ref === parsed.data.visitId);
      if (!visit) return NOT_FOUND("visit");
      const left = inv.lines.filter((l) => l.visitId !== visit.ref);
      const r = invoiceRow(inv);
      const check = takeOffCheck(
        {
          status: inv.status,
          billKind: null,
          visitCount: inv.visits.length,
          leftCents: left.reduce((t, l) => t + l.amountCents, 0),
          leftLines: left.length,
          tipCents: inv.tipCents,
          paidCents: r.paidCents,
        },
        gate("invoice.edit_paid"),
      );
      if (!check.ok) return fail(check.status, check.code, check.message);
      const made = newInvoice(inv.companyId, inv.clientId, visit.jobName);
      made.lines = inv.lines.filter((l) => l.visitId === visit.ref);
      made.visits = [visit];
      made.payNote = "Taken off another invoice";
      inv.lines = left;
      inv.visits = inv.visits.filter((v) => v.ref !== visit.ref);
      ctx.log("invoice.take_off");
      return ok(invoiceDetail(made));
    }
    if (!sub && method === "PATCH") {
      if (!gate("invoice.update")) return NOT_ALLOWED();
      const parsed = invoicePatch.safeParse(b);
      if (!parsed.success) return bad(parsed.error);
      if (inv.status === "paid" && parsed.data.lines)
        return fail(409, "invoice_paid", "This invoice is paid. Use Edit after paid.");
      if (parsed.data.poNumber && !isBusiness({ companyName: clientOf(inv).companyName }))
        return fail(422, "po_business_only", "PO numbers are for business clients only.");
      if (parsed.data.lines) {
        const next = parsed.data.lines.map((l) => ({
          id: newId(),
          visitId: null,
          description: l.description,
          note: l.note ?? null,
          quantity: l.quantity,
          unitPriceCents: l.unitPriceCents,
          amountCents: Math.round(l.quantity * l.unitPriceCents),
          serviceDate: l.serviceDate ?? null,
          propertyLabel: null,
        }));
        if (invoiceTotals(next, inv.tipCents, 0).totalCents < paidOf(inv))
          return fail(422, "below_paid", "The new total is less than what was paid. Lower it after paid instead.");
        inv.lines = next;
      }
      if (parsed.data.crew !== undefined) {
        if (inv.visits.length)
          return fail(409, "invoice_has_visits", "This invoice bills visits. Set who worked each visit on the visit.");
        const crew = crewChips(ctx, parsed.data.crew);
        if (!crew) return fail(422, "not_on_team", "Pick people from your team list.");
        inv.crew = crew;
        ctx.log("invoice.crew");
      }
      if (parsed.data.poNumber !== undefined) inv.poNumber = parsed.data.poNumber;
      if (parsed.data.noteToClient !== undefined) inv.noteToClient = parsed.data.noteToClient;
      if (parsed.data.debt !== undefined) inv.debt = parsed.data.debt;
      if (parsed.data.remindersOff !== undefined) inv.remindersOff = parsed.data.remindersOff;
      if (parsed.data.checkMailed !== undefined) inv.checkMailedAt = parsed.data.checkMailed ? new Date().toISOString() : null;
      if (parsed.data.payWays !== undefined) inv.payWays = parsed.data.payWays;
      settle(inv);
      ctx.log("invoice.update");
      return ok(invoiceDetail(inv));
    }
    if (sub === "edit-paid" && method === "POST") {
      if (!gate("invoice.edit_paid")) return NOT_ALLOWED();
      const parsed = invoiceEditPaidBody.safeParse(b);
      if (!parsed.success) return bad(parsed.error);
      const paid = paidOf(inv);
      const from = totalsOf(inv).totalCents;
      const next = parsed.data.lines.map((l) => ({
        id: newId(),
        visitId: null,
        description: l.description,
        note: l.note ?? null,
        quantity: l.quantity,
        unitPriceCents: l.unitPriceCents,
        amountCents: Math.round(l.quantity * l.unitPriceCents),
        serviceDate: l.serviceDate ?? null,
        propertyLabel: null,
      }));
      const to = invoiceTotals(next, inv.tipCents, 0).totalCents;
      const r = editPaid({
        paidCents: paid,
        fromTotalCents: from,
        toTotalCents: to,
        ...(parsed.data.choice ? { choice: parsed.data.choice } : {}),
      });
      if (r.kind === "needs_choice")
        return fail(422, "credit_choice_needed", "Pick Credit or Don't credit for the money already paid.", {
          overCents: r.overCents,
        });
      inv.lines = next;
      if (r.kind === "credit") {
        credits.set(inv.clientId, (credits.get(inv.clientId) ?? 0) + r.creditCents);
        payments.push({
          companyId: cid,
          id: newId(),
          invoiceId: inv.id,
          clientId: inv.clientId,
          amountCents: -r.creditCents,
          tipCents: 0,
          method: "credit",
          checkNumber: null,
          receivedAt: new Date().toISOString(),
          recordedBy: ctx.me,
          note: "Moved to account credit",
        });
      }
      if (r.kind === "no_credit") inv.payNote = r.note;
      inv.edits.push({
        kind: r.kind,
        fromTotalCents: from,
        toTotalCents: to,
        what: null,
        by: ctx.me,
        at: new Date().toISOString(),
      });
      if (inv.status === "paid" && totalsOf(inv).balanceCents > 0) inv.status = "sent";
      settle(inv);
      ctx.log("invoice.edit_paid");
      return ok(invoiceDetail(inv));
    }
    if (sub === "send" && method === "GET") {
      if (!gate("invoice.send")) return NOT_ALLOWED();
      return ok(previewOf(inv, ctx.brand, query.get("via") === "email" ? "email" : "text"));
    }
    if (sub === "send" && method === "POST") {
      if (!gate("invoice.send")) return NOT_ALLOWED();
      const parsed = sendBody.safeParse(b);
      if (!parsed.success) return bad(parsed.error);
      if (inv.status === "void") return fail(409, "invoice_void", "This invoice is void. Nothing was sent.");
      const pv = previewOf(inv, ctx.brand, parsed.data.via);
      markSent(inv, ctx.brand, parsed.data.via, parsed.data.to);
      ctx.queue({
        channel: parsed.data.via === "text" ? "sms" : "email",
        to: parsed.data.to,
        subject: pv.subject,
        body: pv.body,
        template: "invoice",
      });
      ctx.log("invoice.send");
      return ok(invoiceDetail(inv));
    }
    if (sub === "mark-sent" && method === "POST") {
      if (!gate("invoice.send")) return NOT_ALLOWED();
      markSent(inv, ctx.brand, null, null);
      ctx.log("invoice.mark_sent");
      return ok(invoiceDetail(inv));
    }
    if (sub === "no-charge" && method === "POST") {
      if (!gate("invoice.update")) return NOT_ALLOWED();
      if (paidOf(inv) > 0) return fail(409, "invoice_has_payments", "This invoice has payments on it.");
      inv.status = "no_charge";
      ctx.log("invoice.no_charge");
      return ok(invoiceDetail(inv));
    }
    if (sub === "void" && method === "POST") {
      if (!gate("invoice.update")) return NOT_ALLOWED();
      if (paidOf(inv) > 0) return fail(409, "invoice_has_payments", "This invoice has payments. Edit it instead of voiding it.");
      inv.status = "void";
      ctx.log("invoice.void");
      return ok(invoiceDetail(inv));
    }
    if (sub === "payments" && method === "POST") {
      if (!gate("payment.record")) return NOT_ALLOWED();
      const parsed = recordPaymentBody.safeParse(b);
      if (!parsed.success) return bad(parsed.error);
      if (inv.status === "to_check") return fail(409, "invoice_to_check", "Send or mark this invoice sent first.");
      const balance = totalsOf(inv).balanceCents;
      if (parsed.data.amountCents > balance) return fail(422, "more_than_balance", "That is more than the balance.");
      pay(inv, parsed.data.amountCents, 0, parsed.data.method, ctx.me, parsed.data.note ?? null, parsed.data.checkNumber ?? null);
      ctx.log("payment.record");
      return ok(invoiceDetail(inv));
    }
    if (sub === "charge" && method === "POST") {
      if (!gate("card.charge")) return NOT_ALLOWED();
      const parsed = chargeCardBody.safeParse(b);
      if (!parsed.success) return bad(parsed.error);
      const card = cards.get(inv.clientId);
      if (!card) return fail(409, "no_card", "This client has no saved card.");
      if (card.last4 !== parsed.data.last4)
        return fail(409, "card_changed", "The saved card changed. Look again before you charge.");
      if (parsed.data.amountCents !== totalsOf(inv).balanceCents)
        return fail(409, "amount_changed", "The balance changed. Look again before you charge.");
      if (!ctx.brand.stripe) return fail(409, "stripe_not_set", "Save the Stripe test key in Settings first.");
      pay(inv, parsed.data.amountCents, 0, "card", ctx.me, `Charged ${card.brand} ending ${card.last4}`);
      ctx.log("card.charge");
      return ok(invoiceDetail(inv));
    }
    if (sub === "pdf") return fail(501, "not_built", "PDFs come from the server. The preview has no server.");
    return null;
  }
  if (method === "PATCH" && seg[0] === "clients" && seg[2] === "billing") {
    if (!gate("card.charge")) return NOT_ALLOWED();
    const body = b as { autoCharge?: boolean; autoChargeAgreedOn?: string | null };
    if (body.autoCharge && !body.autoChargeAgreedOn)
      return fail(422, "agreed_date_needed", "Type the date the client said yes in writing.");
    if (body.autoCharge !== undefined) autoCharge.set(seg[1] ?? "", body.autoCharge);
    return ok({ ok: true });
  }
  // Take card by phone: no Stripe in the preview, so the test card 4242 is saved.
  if (method === "POST" && seg[0] === "clients" && seg[2] === "card") {
    if (!gate("payment.record")) return NOT_ALLOWED();
    if (seg[3] === "start")
      return ok({
        intentId: `seti_preview_${seg[1] ?? ""}`,
        clientSecret: "seti_preview_secret",
        publishableKey: null,
        fake: true,
      });
    if (seg[3] === "done") {
      cards.set(seg[1] ?? "", { brand: "Visa", last4: "4242", expMonth: 12, expYear: 2030 });
      return ok({ saved: true });
    }
  }

  // Quotes and leads
  if (route === "GET quotes") {
    if (!gate("quote.read")) return NOT_ALLOWED();
    const status = query.get("status") ?? "all";
    return ok({
      quotes: quotes
        .filter((q) => q.companyId === cid && !q.archived)
        .filter((q) => !query.get("clientId") || q.clientId === query.get("clientId"))
        .map(quoteRow)
        .filter((r) => status === "all" || r.status === status)
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
    });
  }
  if (route === "POST quotes") {
    if (!gate("quote.update")) return NOT_ALLOWED();
    const parsed = quoteInput.safeParse(b);
    if (!parsed.success) return bad(parsed.error);
    const d = parsed.data;
    const q: Quote = {
      companyId: cid,
      id: newId(),
      number: nextNumber(cid, "quote"),
      clientId: d.clientId ?? null,
      leadId: d.leadId ?? null,
      contactName: d.contactName ?? null,
      contactPhone: d.contactPhone ?? null,
      contactEmail: d.contactEmail ?? null,
      address: d.address ?? null,
      title: d.title ?? null,
      message: d.message ?? null,
      status: "draft",
      expiresOn: d.expiresOn ?? addDays(TODAY, 30),
      deposit: d.deposit ?? null,
      frequency: d.frequency,
      lines: qLines(d.lines),
      token: token(),
      sentAt: null,
      approvedAt: null,
      signature: null,
      jobId: null,
      depositInvoiceId: null,
      createdAt: new Date().toISOString(),
    };
    quotes.push(q);
    const lead = leads.find((l) => l.id === q.leadId);
    if (lead) lead.quoteId = q.id;
    ctx.log("quote.create");
    return ok(quoteDetail(q), 201);
  }
  // Requests: each lead with its quote, as the server's requests.ts does.
  const requestRow = (l: Lead): RequestRow => {
    const live = quotes.filter((x) => !x.archived);
    const q = live.find((x) => x.id === l.quoteId) ?? live.find((x) => x.leadId === l.id);
    const status = q ? quoteStatus(q) : null;
    const state = requestState({ stageFixed: stagesOf(cid).find((s) => s.id === l.stageId)?.fixed ?? null, quoteStatus: status });
    return {
      id: l.id,
      name: l.name,
      phone: l.phone,
      email: l.email,
      address: l.address,
      service: l.service,
      valueCents: l.valueCents,
      source: l.source,
      clientId: l.clientId,
      state,
      archived: requestArchived({ removed: Boolean(l.archived), state }),
      quote:
        q && status
          ? { id: q.id, number: q.number, status, totalCents: quoteSums(q).totalCents, repeatCents: null, frequency: q.frequency }
          : null,
      createdAt: l.createdAt,
    };
  };
  if (route === "GET requests") {
    if (!gate("lead.read")) return NOT_ALLOWED();
    return ok({
      requests: leads
        .filter((l) => l.companyId === cid && (!query.get("clientId") || l.clientId === query.get("clientId")))
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
        .map(requestRow),
    });
  }
  if (seg[0] === "requests" && seg[1]) {
    const lead = leads.find((l) => l.id === seg[1] && l.companyId === cid);
    if (method === "GET" && !seg[2]) {
      if (!gate("lead.read")) return NOT_ALLOWED();
      if (!lead) return NOT_FOUND("request");
      const detail: RequestDetail = {
        ...requestRow(lead),
        note: lead.note,
        stage: stagesOf(cid).find((s) => s.id === lead.stageId)?.name ?? "",
        utm: {},
        notes: leadNotes.get(lead.id) ?? [],
        consent: [],
        replies: [],
        photos: [],
        // One made-up answered call, so the preview shows What they said on the call.
        calls:
          lead.phone === "+15045550184"
            ? [
                {
                  id: "c0000000-0000-4000-8000-000000000184",
                  direction: "in",
                  at: lead.createdAt,
                  seconds: 245,
                  status: "done",
                  summary: ["They want a deep clean of a 3 bedroom house before a family visit.", "They have two dogs."],
                  nextSteps: ["Send a quote by Friday."],
                  words: [
                    { who: "them", text: "Hi, I need a deep clean before my family comes in.", at: 2 },
                    { who: "us", text: "Happy to help. How many bedrooms and baths?", at: 6 },
                    { who: "them", text: "Three bedrooms, two baths. And two dogs.", at: 10 },
                  ],
                },
              ]
            : [],
      };
      return ok(detail);
    }
    if (method === "POST" && seg[2]) {
      if (!gate("lead.update")) return NOT_ALLOWED();
      if (!lead) return NOT_FOUND("request");
      if (seg[2] === "notes") {
        const body = typeof (b as { body?: unknown }).body === "string" ? (b as { body: string }).body.trim() : "";
        if (!body) return fail(400, "bad_request", "Type the note.");
        const note: NoteRow = {
          id: newId(),
          body,
          visibility: "office",
          pinned: false,
          authorName: "You",
          createdAt: new Date().toISOString(),
        };
        leadNotes.set(lead.id, [note, ...(leadNotes.get(lead.id) ?? [])]);
        return ok(note, 201);
      }
      if (seg[2] === "archive" || seg[2] === "restore") lead.archived = seg[2] === "archive";
      else if (seg[2] === "decline") {
        const row = requestRow(lead);
        if (row.state === "converted") return fail(409, "request_converted", "This request is already approved work.");
        lead.stageId = stagesOf(cid).find((s) => s.fixed === "lost")?.id ?? lead.stageId;
        lead.stageChangedAt = new Date().toISOString();
        const q = quotes.find((x) => x.id === row.quote?.id);
        if (q && (q.status === "draft" || q.status === "sent")) q.status = "declined";
      } else return NOT_FOUND("action");
      ctx.log(`request.${seg[2]}`);
      return ok(requestRow(lead));
    }
  }
  if (route === "GET pipeline") {
    if (!gate("lead.read")) return NOT_ALLOWED();
    const now = new Date();
    const list = stagesOf(cid);
    const rulesOf = (id: string) => list.find((s) => s.id === id)?.rules ?? NO_STAGE_RULES;
    // The server moves leads every 15 minutes; the preview has no timer, so it moves them when the board loads.
    for (const l of leads.filter((x) => x.companyId === cid && !x.archived)) {
      const r = rulesOf(l.stageId);
      if (dueToMove(r, l.stageChangedAt, now))
        moveLead(
          l.id,
          list.find((s) => s.id === r.moveToStageId),
        );
    }
    const older = query.get("older") === "1";
    const open = leads.filter((l) => l.companyId === cid && !l.archived);
    const hidden: Record<string, number> = {};
    const shown = open.filter((l) => {
      if (older || !hiddenByRules(rulesOf(l.stageId), l.stageChangedAt, now)) return true;
      hidden[l.stageId] = (hidden[l.stageId] ?? 0) + 1;
      return false;
    });
    return ok({
      stages: list.map((s) => ({
        id: s.id,
        name: s.name,
        position: s.position,
        fixed: s.fixed,
        rules: s.rules ?? NO_STAGE_RULES,
      })),
      leads: shown
        .sort((a, b) => b.stageChangedAt.localeCompare(a.stageChangedAt))
        .map((l) => ({ ...leadRow(l), assignedName: l.assignedTo ? "Kenia Ramos" : null, quote: requestRow(l).quote })),
      hidden,
    });
  }
  if (method === "PATCH" && seg[0] === "pipeline" && seg[1] === "stages" && seg[2]) {
    if (!gate("lead.update")) return NOT_ALLOWED();
    const st = stagesOf(cid).find((s) => s.id === seg[2]);
    if (!st) return NOT_FOUND("column");
    const parsed = stageRulesShape.safeParse(b);
    if (!parsed.success) return bad(parsed.error);
    if (Boolean(parsed.data.moveAfterDays) !== Boolean(parsed.data.moveToStageId))
      return fail(400, "bad_request", "Give both the days and the column to move them to, or neither.");
    st.rules = stageRulesOf(parsed.data);
    return ok({ ok: true });
  }
  if (route === "GET quotes/stats") {
    if (!gate("quote.read")) return NOT_ALLOWED();
    return ok(quoteStats(quotes.filter((q) => q.companyId === cid && !q.archived).map(quoteRow), new Date()));
  }
  if (route === "POST leads") {
    if (!gate("lead.update")) return NOT_ALLOWED();
    const parsed = leadInput.safeParse(b);
    if (!parsed.success) return bad(parsed.error);
    const d = parsed.data;
    const now = new Date().toISOString();
    const lead: Lead = {
      companyId: cid,
      id: newId(),
      name: d.name,
      phone: d.phone ?? null,
      email: d.email ?? null,
      address: d.address ?? null,
      service: d.service ?? null,
      valueCents: d.valueCents ?? null,
      source: d.source ?? null,
      stageId: d.stageId ?? stagesOf(cid)[0]?.id ?? "",
      clientId: null,
      quoteId: null,
      note: d.note ?? null,
      businessName: d.businessName ?? null,
      website: d.website ?? null,
      assignedTo: d.assignedTo ?? null,
      stageChangedAt: now,
      createdAt: now,
    };
    leads.push(lead);
    ctx.log("lead.create");
    return ok(leadRow(lead), 201);
  }
  if (method === "PATCH" && seg[0] === "leads" && seg[1]) {
    if (!gate("lead.update")) return NOT_ALLOWED();
    const lead = leads.find((l) => l.id === seg[1] && l.companyId === cid);
    if (!lead) return NOT_FOUND("lead");
    const parsed = leadPatch.safeParse(b);
    if (!parsed.success) return bad(parsed.error);
    const { stageId, ...rest } = parsed.data;
    if (stageId) {
      const st = stagesOf(cid).find((s) => s.id === stageId);
      if (!st) return NOT_FOUND("stage");
      moveLead(lead.id, st);
    }
    for (const [k, v] of Object.entries(rest)) if (v !== undefined) Object.assign(lead, { [k]: v });
    return ok(leadRow(lead));
  }
  if (seg[0] === "quotes" && seg[1]) {
    const q = quotes.find((x) => x.id === seg[1] && x.companyId === cid);
    if (!q) return NOT_FOUND("quote");
    const sub = seg[2];
    if (!sub && method === "GET") return gate("quote.read") ? ok(quoteDetail(q)) : NOT_ALLOWED();
    if ((sub === "archive" || sub === "restore") && method === "POST") {
      if (!gate("quote.update")) return NOT_ALLOWED();
      if (q.status === "converted") return fail(409, "quote_converted", "This quote is already a job. It stays.");
      q.archived = sub === "archive";
      return ok({ ok: true });
    }
    if (!gate(sub === "convert" ? "job.update" : "quote.update")) return NOT_ALLOWED();
    if (sub === "files" && method === "POST") {
      const f = b as { name?: string; mime?: string; dataBase64?: string };
      if (!isSectionFile(f.mime ?? ""))
        return fail(415, "file_type", "Add a JPEG, PNG, WEBP, GIF, AVIF, HEIC, PDF or Word file.");
      const fid = newId();
      const size = Math.round(((f.dataBase64 ?? "").length * 3) / 4);
      quoteFiles.set(fid, {
        id: fid,
        quoteId: q.id,
        name: f.name ?? "file",
        mime: f.mime ?? "",
        size,
        data: `data:${f.mime ?? ""};base64,${f.dataBase64 ?? ""}`,
      });
      const kind = (f.mime ?? "").startsWith("image/") ? "photo" : "other";
      return ok({ id: fid, name: f.name ?? "file", mime: f.mime ?? "", size, kind, createdAt: new Date().toISOString() }, 201);
    }
    if (sub === "sections" && method === "PUT") {
      const parsed = quoteSectionsBody.safeParse(b);
      if (!parsed.success) return bad(parsed.error);
      if (sectionFileIds(parsed.data.sections).some((fid) => quoteFiles.get(fid)?.quoteId !== q.id))
        return fail(422, "file_missing", "A file in that section is gone. Add it again.");
      q.sections = parsed.data.sections;
      q.message = parsed.data.message;
      return ok(quoteDetail(q));
    }
    if (sub === "header" && method === "PATCH") {
      const parsed = quoteHeaderBody.safeParse(b);
      if (!parsed.success) return bad(parsed.error);
      if (quotes.some((x) => x.companyId === cid && x.id !== q.id && x.number === parsed.data.number))
        return fail(409, "number_taken", `Quote #${String(parsed.data.number)} is already used. Pick another number.`);
      q.title = parsed.data.title;
      q.number = parsed.data.number;
      return ok(quoteDetail(q));
    }
    if (!sub && method === "PATCH") {
      if (q.status === "approved" || q.status === "converted")
        return fail(409, "quote_locked", "This quote is approved. Make a new quote to change it.");
      const parsed = quotePatch.safeParse(b);
      if (!parsed.success) return bad(parsed.error);
      const { lines, ...rest } = parsed.data;
      for (const [k, v] of Object.entries(rest)) if (v !== undefined) Object.assign(q, { [k]: v });
      if (lines) q.lines = qLines(lines);
      return ok(quoteDetail(q));
    }
    if (sub === "send" && method === "POST") {
      const parsed = quoteSendBody.safeParse(b);
      if (!parsed.success) return bad(parsed.error);
      if (q.status === "converted" || q.status === "declined")
        return fail(409, "quote_closed", "This quote is closed. Nothing was sent.");
      const first = (q.contactName ?? quoteName(q)).split(/\s+/)[0] ?? "there";
      ctx.queue({
        channel: parsed.data.via === "text" ? "sms" : "email",
        to: parsed.data.to,
        subject: parsed.data.via === "email" ? `Your quote from ${ctx.brand.name}` : null,
        body: quoteMessage({ brand: ctx.brand.name, first, title: q.title }, appLink("quote", q.token)),
        template: "quote",
      });
      if (q.status === "draft") q.status = "sent";
      q.sentAt = new Date().toISOString();
      moveLead(q.leadId, stageNamed(cid, "Quote sent"));
      ctx.log("quote.send");
      return ok(quoteDetail(q));
    }
    if (sub === "decline" && method === "POST") {
      if (q.status === "converted") return fail(409, "quote_converted", "This quote is already a job.");
      q.status = "declined";
      moveLead(q.leadId, stageNamed(cid, "Lost"));
      return ok(quoteDetail(q));
    }
    if (sub === "convert" && method === "POST") {
      const parsed = quoteConvertBody.safeParse(b);
      if (!parsed.success) return bad(parsed.error);
      if (q.status === "converted" && q.jobId && q.clientId) return ok({ jobId: q.jobId, clientId: q.clientId });
      if (q.status !== "approved" || !q.clientId)
        return fail(409, "quote_not_approved", "Only an approved quote can become a job.");
      const jobId = workLookup.addJob(
        q.clientId,
        q.title ?? "Job",
        q.frequency,
        parsed.data.startDate,
        parsed.data.startTime ?? null,
        q.lines.filter((l) => !l.optional || l.picked),
      );
      if (!jobId) return NOT_FOUND("client");
      q.jobId = jobId;
      q.status = "converted";
      ctx.log("quote.convert");
      return ok({ jobId, clientId: q.clientId });
    }
    if (sub === "pdf") return fail(501, "not_built", "PDFs come from the server. The preview has no server.");
    return null;
  }
  return null;
}

// Made-up records ---------------------------------------------------------------------------------------------------

export function seedMoney(opts: {
  id: () => string;
  today: string;
  companyId: string;
  brand: MoneyBrand;
  /** Who worked the preview's invoice typed by hand. */
  handCrew: { id: string; name: string } | null;
}): void {
  newId = opts.id;
  TODAY = opts.today;
  visitHooks.done = billVisit;
  visitHooks.reopened = unbillVisit;
  clientMoneyHook.facts = (clientId) => {
    const paid = payments.filter((p) => p.clientId === clientId && p.method !== "credit");
    const theirs = invoices.filter((i) => i.clientId === clientId && i.status !== "void");
    const qs = quotes.filter((q) => q.clientId === clientId);
    const newest = (xs: (string | null)[]) =>
      xs
        .filter((x): x is string => Boolean(x))
        .sort()
        .at(-1) ?? null;
    const late = theirs
      .filter((i) => (i.status === "sent" || i.status === "to_collect") && i.dueAt && totalsOf(i).balanceCents > 0)
      .map((i) => Math.floor((Date.now() - new Date(i.dueAt ?? "").getTime()) / 86_400_000));
    return {
      lifetimeCents: paid.reduce((a, p) => a + (p.amountCents > 0 ? p.amountCents - p.tipCents : p.amountCents), 0),
      times: {
        invoice_paid: newest(paid.filter((p) => p.amountCents > 0).map((p) => p.receivedAt)),
        invoice_sent: newest(theirs.map((i) => i.sentAt)),
        quote_sent: newest(qs.map((q) => q.sentAt)),
        quote_approved: newest(qs.map((q) => q.approvedAt)),
      },
      pastDueDays: Math.max(0, ...late),
    };
  };
  clientMoneyHook.leadsBetween = (companyId, from, to) =>
    leads.filter((l) => l.companyId === companyId && l.createdAt.slice(0, 10) >= from && l.createdAt.slice(0, 10) <= to).length;
  const cid = opts.companyId;
  for (const v of workLookup.doneVisits(cid)) billVisit(v);
  const people = workLookup.clients(cid);
  const [a, b, c, d, e, f] = people;
  const made = (who: WorkClient | undefined, job: string, cents: number, sentDaysAgo: number) => {
    if (!who) return null;
    const inv = newInvoice(cid, who.id, job);
    const date = addDays(TODAY, -sentDaysAgo - 1);
    inv.visits.push({ ref: newId(), date, jobName: job });
    inv.lines.push({
      id: newId(),
      visitId: null,
      description: job,
      note: null,
      quantity: 1,
      unitPriceCents: cents,
      amountCents: cents,
      serviceDate: date,
      propertyLabel: null,
    });
    inv.number = nextNumber(cid, "invoice");
    inv.firstSentAt = iso(sentDaysAgo, 15);
    inv.sentAt = inv.firstSentAt;
    inv.sentVia = "text";
    inv.sentTo = who.phone;
    inv.issuedOn = addDays(TODAY, -sentDaysAgo);
    inv.dueAt = pastDueAt(new Date(inv.firstSentAt), false, null, opts.brand).toISOString();
    inv.status = "sent";
    return inv;
  };
  const paidCard = made(a, "Weekly House Cleaning", 16000, 6);
  if (paidCard && a) {
    pay(paidCard, 16000, 2400, "card", null);
    payments.at(-1)!.receivedAt = iso(5, 18);
    cards.set(a.id, { brand: "Visa", last4: "4242", expMonth: 8, expYear: 2029 });
  }
  const cash = made(b, "Deep House Cleaning", 28500, 3);
  if (cash) {
    pay(cash, 28500, 0, "check", "Halfi R.", null, "1184");
    payments.at(-1)!.receivedAt = iso(2, 11);
  }
  made(c, "Bi-Weekly House Cleaning", 14500, 4);
  made(d, "Move Out Cleaning", 42000, 0);
  const late = made(e, "Weekly House Cleaning", 15000, 9);
  if (late && e) cards.set(e.id, { brand: "Mastercard", last4: "5454", expMonth: 3, expYear: 2028 });
  if (f) credits.set(f.id, 2500);
  // An invoice typed by hand (no visit), paid in cash five days ago, worked by one of the crew (question 43).
  if (c && opts.handCrew) {
    const hand = newInvoice(cid, c.id, null);
    hand.lines.push({
      id: newId(),
      visitId: null,
      description: "Inside windows",
      note: null,
      quantity: 1,
      unitPriceCents: 9500,
      amountCents: 9500,
      serviceDate: addDays(TODAY, -5),
      propertyLabel: null,
    });
    hand.crew = [opts.handCrew];
    hand.number = nextNumber(cid, "invoice");
    hand.issuedOn = addDays(TODAY, -5);
    hand.status = "sent";
    pay(hand, 9500, 0, "cash", "Olivia G.");
    payments.at(-1)!.receivedAt = iso(5, 16);
  }

  // Leads and quotes
  const st = stagesOf(cid);
  const lead = (
    name: string,
    phone: string,
    service: string,
    valueCents: number,
    stage: number,
    hoursAgo: number,
    source: string,
  ) => {
    const at = new Date(Date.now() - hoursAgo * 3_600_000).toISOString();
    const l: Lead = {
      companyId: cid,
      id: newId(),
      name,
      phone,
      email: null,
      address: null,
      service,
      valueCents,
      source,
      stageId: st[stage]?.id ?? "",
      clientId: null,
      quoteId: null,
      note: null,
      stageChangedAt: at,
      createdAt: at,
    };
    leads.push(l);
    return l;
  };
  const tasha = Object.assign(lead("Tasha Boudreaux", "+15045550181", "Deep House Cleaning", 32000, 0, 1, "Google"), {
    email: "tasha.b@example.com",
    address: "812 Esplanade Ave, New Orleans 70116",
    note: [
      "Cleaners Get Started",
      "3 bed, 2 bath",
      "Found us through: Google",
      "How many bedrooms?: 3",
      "How many bathrooms?: 2",
      "Is this for a home or a business?: Home",
      "Any pets?: One dog",
      "Anything we should know? (optional): Please use the side gate. The dog is friendly but loud.",
      "Time of day flexible",
    ].join("\n"),
  });
  lead("Marcus Lee", "+15045550182", "Move Out Cleaning", 45000, 0, 5, "Facebook");
  // A client's own words from My Trades: the title is the first 77 letters and the note keeps them all.
  const asked =
    "They did a great job on my first deep clean, thank you! I'd love a regular clean any day 11.12 thru 11.19. No dishes or laundry but the stovetop needs it.";
  Object.assign(lead("Ana Roy", "+15045550186", `${asked.slice(0, 77)}...`, 0, 0, 7, "my_trades"), {
    email: "ana.roy@example.com",
    address: "716 Elysian Fields Ave, New Orleans 70117",
    note: ["Sent from My Trades by a client.", "Wants 2026-11-16", asked].join("\n"),
  });
  Object.assign(lead("Dana Pitre", "+15045550183", "Weekly House Cleaning", 15000, 1, 30, "Website"), {
    address: "4410 Magazine St, New Orleans 70115",
    assignedTo: newId(),
  });
  // A long board: more new leads, and old won and lost ones the columns' hide rule keeps off.
  (
    [
      ["Gina Lamarque", "Standard Clean", 17500, 0, 20],
      ["Paul Hebert", "Deep Clean", 28000, 0, 40],
      ["Rita Arceneaux", "Move In Cleaning", 36000, 1, 70],
      ["Owen Brady", "Bi-Weekly House Cleaning", 15000, 3, 24 * 3],
      ["Ivy Chen", "Deep Clean", 30000, 3, 24 * 45],
      ["Sam Duhon", "Standard Clean", 16000, 3, 24 * 60],
      ["Nora Pierce", "Move Out Cleaning", 40000, 4, 24 * 50],
      ["Leo Gautreaux", "Deep Clean", 26000, 4, 24 * 75],
    ] as const
  ).forEach(([n, s, v, stage, h], i) => lead(n, `+1504555019${String(i)}`, s, v, stage, h, i % 2 ? "Google" : "Website"));
  const won = st.find((s) => s.fixed === "won");
  const lost = st.find((s) => s.fixed === "lost");
  if (won) won.rules = { ...NO_STAGE_RULES, hideAfterDays: 30 };
  if (lost) lost.rules = { ...NO_STAGE_RULES, hideAfterDays: 30 };
  const sentStage = st[2];
  if (sentStage) sentStage.rules = { ...NO_STAGE_RULES, followUpDays: 2 };
  const sentLead = lead("Ray Guidry", "+15045550184", "Post Construction Clean", 90000, 2, 52, "Referral");
  lead("Lena Fontenot", "+15045550185", "Bi-Weekly House Cleaning", 14000, 4, 200, "Google");

  const quote = (
    who: { clientId?: string; name?: string; phone?: string; leadId?: string },
    title: string,
    lines: [string, number, boolean?][],
    status: QuoteStatus,
    daysAgo: number,
    deposit: Deposit | null = null,
  ) => {
    const q: Quote = {
      companyId: cid,
      id: newId(),
      number: nextNumber(cid, "quote"),
      clientId: who.clientId ?? null,
      leadId: who.leadId ?? null,
      contactName: who.name ?? null,
      contactPhone: who.phone ?? null,
      contactEmail: null,
      address: null,
      title,
      message: "Thanks for asking us. Here is what we would do and what it costs.",
      status,
      expiresOn: addDays(TODAY, 30 - daysAgo),
      deposit,
      frequency: "one-time",
      lines: lines.map(([description, cents, optional]) => ({
        id: newId(),
        description,
        note: null,
        quantity: 1,
        unitPriceCents: cents,
        amountCents: cents,
        optional: optional ?? false,
        picked: !optional,
        recurring: false,
        bill: "once" as const,
      })),
      token: token(),
      sentAt: status === "draft" ? null : iso(daysAgo, 14),
      approvedAt: status === "approved" ? iso(daysAgo - 1, 9) : null,
      signature: status === "approved" ? { name: who.name ?? "Client", at: iso(daysAgo - 1, 9), drawing: null } : null,
      jobId: null,
      depositInvoiceId: null,
      createdAt: iso(daysAgo, 13),
    };
    quotes.push(q);
    return q;
  };
  const rq = quote(
    { name: "Ray Guidry", phone: "+15045550184", leadId: sentLead.id },
    "Post Construction Clean",
    [
      ["Post construction clean, 3 bed house", 85000],
      ["Inside all windows", 12000, true],
      ["Haul away debris", 9000, true],
    ],
    "sent",
    2,
    { kind: "percent", value: 20 },
  );
  sentLead.quoteId = rq.id;
  if (c)
    quote(
      { clientId: c.id, name: c.name },
      "Deep House Cleaning",
      [
        ["Deep house cleaning", 32000],
        ["Inside the oven", 4500, true],
      ],
      "approved",
      4,
    );
  quote(
    { name: "Tasha Boudreaux", phone: "+15045550181", leadId: tasha.id },
    "Deep House Cleaning",
    [["Deep house cleaning", 32000]],
    "draft",
    0,
  );
  quote({ name: "Ben Arceneaux", phone: "+15045550186" }, "Move Out Cleaning", [["Move out cleaning", 38000]], "declined", 12);
}

// Proposals (WORK > Proposals) ----------------------------------------------------------------------------------------

interface StoredProposal {
  building: string;
  walk: ProposalWalk;
  words: ProposalWords;
  rating: string;
  reviews: string[];
  savedAt: string;
}
const proposalStore = new Map<string, StoredProposal>();

function proposalPage(q: Quote, brand: MoneyBrand) {
  const p = proposalStore.get(q.id);
  if (!p) return null;
  return proposalPublic({
    walk: p.walk,
    words: p.words,
    building: p.building,
    contactName: q.contactName ?? quoteName(q),
    address: q.address,
    brand: brand.name,
    date: new Date(p.savedAt).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }),
    rating: p.rating,
    reviews: p.reviews,
  });
}

/** As the server's proposals/proposals.ts: priced here from the walkthrough and the company's proposal settings. */
export function proposalsRoute(ctx: MoneyCtx, method: string, p: string, b: unknown, s: ProposalSettings): Answer | null {
  const seg = p.split("/");
  if (seg[0] !== "proposals" || seg.length > 2) return null;
  const cid = ctx.companyId;
  const mine = (id: string) => quotes.find((q) => q.id === id && q.companyId === cid && proposalStore.has(q.id));
  if (method === "GET" && seg.length === 1) {
    if (!ctx.gate("quote.read")) return NOT_ALLOWED();
    const rows: ProposalRow[] = quotes
      .filter((q) => q.companyId === cid && proposalStore.has(q.id))
      .sort((a, z) => z.createdAt.localeCompare(a.createdAt))
      .map((q) => ({
        quoteId: q.id,
        number: q.number,
        business: proposalStore.get(q.id)?.building ?? quoteName(q),
        contactName: q.contactName ?? quoteName(q),
        service: proposalStore.get(q.id)?.walk.service ?? "janitorial",
        status: quoteStatus(q),
        totalCents: quoteSums(q).totalCents,
        monthly: q.frequency !== "one-time",
        archived: Boolean(q.archived),
        createdAt: q.createdAt,
      }));
    return ok({ proposals: rows });
  }
  if (method === "GET") {
    if (!ctx.gate("quote.read")) return NOT_ALLOWED();
    const q = mine(seg[1] ?? "");
    const sp = q ? proposalStore.get(q.id) : undefined;
    if (!q || !sp) return NOT_FOUND("proposal");
    const out: ProposalDetail = {
      quoteId: q.id,
      number: q.number,
      status: q.status,
      building: sp.building,
      clientId: q.clientId,
      contactName: q.contactName,
      contactPhone: q.contactPhone,
      contactEmail: q.contactEmail,
      address: q.address,
      walk: sp.walk,
    };
    return ok(out);
  }
  if ((method === "POST" && seg.length === 1) || (method === "PUT" && seg.length === 2)) {
    if (!ctx.gate("quote.update")) return NOT_ALLOWED();
    const parsed = proposalSaveBody.safeParse(b);
    if (!parsed.success) return bad(parsed.error);
    const d = parsed.data;
    const quoteId = seg[1] ?? null;
    const skip = (status: number, code: string, message: string) =>
      d.autosave ? ok({ quoteId, saved: false }) : fail(status, code, message);
    const client = d.clientId ? workLookup.client(d.clientId) : null;
    if (d.clientId && !client) return NOT_FOUND("client");
    const company = d.company?.trim() ?? "";
    const contact = d.contactName?.trim() ?? "";
    const co = client?.companyName?.trim() ?? "";
    const building = client ? co || client.name : company || contact;
    if (!building) return skip(422, "missing", "Pick the business, or add it as a new client.");
    if (!client && !d.contactPhone?.trim() && !d.contactEmail)
      return skip(422, "missing", "Pick the client, or add a new one with a mobile number or email.");
    const walk = proposalWalk.parse({
      ...d,
      rooms: d.rooms.filter((r) => r.sqft > 0),
      days: d.service === "construction" || d.freq === "once" ? [] : d.days,
    });
    const fx = walk.fixtures.toilet + walk.fixtures.urinal + walk.fixtures.sink;
    if (!walk.rooms.length && (walk.service === "construction" || !fx))
      return skip(422, "missing", "Add the rooms from the walkthrough first.");
    const calc = proposalCalc(walk, s);
    if (calc.noRate)
      return skip(422, "no_rate", "Maid service uses your hourly rate. Set it in Proposal settings, under Pricing.");
    const chosen = proposalPicked(walk, calc);
    if (!chosen || chosen.option.monthlyCents <= 0)
      return skip(422, "missing", "The rooms add up to no time. Check the square feet.");
    walk.pick = chosen.pick;
    const words = proposalWordsOf(s);
    const lines: QLine[] = proposalLines(walk, calc, chosen.pick, chosen.option).map((l) => ({
      id: newId(),
      description: l.description,
      note: l.note,
      quantity: 1,
      unitPriceCents: l.amountCents,
      amountCents: l.amountCents,
      optional: false,
      picked: true,
      recurring: !calc.once,
      bill: "once",
    }));
    const fields = {
      clientId: client?.id ?? null,
      contactName: client ? null : contact || company,
      contactPhone: client ? null : (d.contactPhone ?? null),
      contactEmail: client ? null : (d.contactEmail ?? null),
      address: client ? null : (d.address ?? null),
      title: proposalTitle(walk.service, calc.freq, building),
      frequency: proposalJobFrequency(calc),
      terms: proposalTerms(words.terms, walk).join("\n") || null,
      lines,
    };
    let q = quoteId ? mine(quoteId) : undefined;
    if (quoteId && !q) return NOT_FOUND("proposal");
    if (q) {
      if (q.status !== "draft") return skip(409, "sent", "This proposal was already sent. Make a new one to change it.");
      Object.assign(q, fields);
    } else {
      q = {
        ...fields,
        companyId: cid,
        id: newId(),
        number: nextNumber(cid, "quote"),
        leadId: null,
        message: null,
        status: "draft",
        expiresOn: addDays(TODAY, 30),
        deposit: null,
        token: token(),
        sentAt: null,
        approvedAt: null,
        signature: null,
        jobId: null,
        depositInvoiceId: null,
        createdAt: new Date().toISOString(),
      };
      quotes.push(q);
    }
    proposalStore.set(q.id, {
      building,
      walk,
      words,
      rating: s.rating,
      reviews: [...s.reviews],
      savedAt: new Date().toISOString(),
    });
    ctx.log("quote.update");
    return ok({ quoteId: q.id, saved: true });
  }
  return null;
}
